import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { COLORS } from '../theme';

const SUSPENSION_TYPES = [
  { value: 'three_strikes', label: 'Three strikes (contact violations)' },
  { value: 'fraud', label: 'Fraud or scam' },
  { value: 'inappropriate_behavior', label: 'Inappropriate behavior' },
  { value: 'spam', label: 'Spam or abuse' },
  { value: 'security_risk', label: 'Security risk' },
  { value: 'accidental', label: 'Accidental — undo' },
  { value: 'other', label: 'Other' },
];

const REACTIVATION_TYPES = [
  { value: 'issue_resolved', label: 'Issue resolved' },
  { value: 'suspended_in_error', label: 'Suspended in error' },
  { value: 'corrected_behavior', label: 'Worker corrected behavior' },
  { value: 'investigation_cleared', label: 'Investigation cleared' },
  { value: 'strikes_forgiven', label: 'Strikes forgiven' },
  { value: 'other', label: 'Other' },
];

const INPUT_STYLE = {
  color: '#1F2937',
  backgroundColor: 'white',
  border: '1px solid #D1D5DB',
  padding: '8px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

function Skeleton() {
  return (
    <div style={{
      height: '14px',
      backgroundColor: COLORS.surfaceHover,
      borderRadius: '4px',
      animation: 'pulse 1.4s ease-in-out infinite',
      width: '80%',
    }} />
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { bg: '#34A85326', text: COLORS.success, label: 'Active' },
    suspended: { bg: '#EA433526', text: COLORS.danger, label: 'Suspended' },
    warned: { bg: '#F5A62326', text: COLORS.warning, label: 'Warned' },
    pending_email_verification: { bg: '#F5A62326', text: COLORS.warning, label: 'Pending' },
  };
  const style = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary, label: status || '—' };
  return (
    <span style={{
      backgroundColor: style.bg,
      color: style.text,
      borderRadius: '4px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: '600',
    }}>
      {style.label}
    </span>
  );
}

function AccessBadge({ level }) {
  if (!level) return <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>—</span>;
  return (
    <span style={{
      backgroundColor: COLORS.surface,
      color: COLORS.accent,
      borderRadius: '4px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: '600',
    }}>
      {level}
    </span>
  );
}

const COL = 'repeat(5, 1fr)';

export default function EmployersPage() {
  const navigate = useNavigate();
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [modal, setModal] = useState(null); // { uid, displayName, mode: 'suspend' | 'reactivate' }
  const [modalType, setModalType] = useState('');
  const [modalReason, setModalReason] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'users'), where('role', '==', 'employer')),
      snap => {
        const sorted = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setEmployers(sorted);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = employers.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (e.displayName || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q);
  });

  function openModal(uid, displayName, mode) {
    setModal({ uid, displayName, mode });
    setModalType('');
    setModalReason('');
    setModalPassword('');
    setModalError('');
  }

  async function submitModal() {
    if (!modalType) { setModalError('Please select a reason.'); return; }
    if (modalReason.trim().length < 10) { setModalError('Details must be at least 10 characters.'); return; }
    if (!modalPassword) { setModalError('Password is required.'); return; }
    setModalLoading(true);
    setModalError('');
    try {
      if (modal.mode === 'suspend') {
        const fn = httpsCallable(functions, 'adminSuspendAccount');
        await fn({ targetUid: modal.uid, password: modalPassword, suspensionType: modalType, suspensionReason: modalReason.trim() });
      } else {
        const fn = httpsCallable(functions, 'adminReactivateAccount');
        await fn({ targetUid: modal.uid, password: modalPassword, reactivationType: modalType, reactivationReason: modalReason.trim() });
      }
      setToast(modal.mode === 'suspend' ? 'Account suspended' : 'Account reactivated');
      setTimeout(() => setToast(''), 3000);
      setModal(null);
    } catch (err) {
      setModalPassword('');
      setModalError(err.message || 'Action failed. Check your password and try again.');
    } finally {
      setModalLoading(false);
    }
  }

  const typeOptions = modal?.mode === 'suspend' ? SUSPENSION_TYPES : REACTIVATION_TYPES;
  const headerCell = { fontSize: '12px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0 0 12px 0', fontWeight: '600' };
  const cell = { padding: '12px 0', fontSize: '13px', color: COLORS.textPrimary };

  return (
    <>
      <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: COLORS.success, color: 'white',
          padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* Suspend / Reactivate Modal */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: COLORS.surface, borderRadius: '12px', padding: '28px',
            width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '6px' }}>
              {modal.mode === 'suspend' ? 'Suspend Account' : 'Reactivate Account'}
            </div>
            <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '20px' }}>
              Re-enter your password and provide a reason. This action will be logged.
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                Reason
              </label>
              <select
                value={modalType}
                onChange={e => setModalType(e.target.value)}
                style={INPUT_STYLE}
              >
                <option value="">Select a reason…</option>
                {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                Details
              </label>
              <textarea
                value={modalReason}
                onChange={e => setModalReason(e.target.value)}
                placeholder="Minimum 10 characters"
                rows={3}
                style={{ ...INPUT_STYLE, resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                Your Password
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={modalPassword}
                onChange={e => setModalPassword(e.target.value)}
                style={INPUT_STYLE}
              />
            </div>

            {modalError && (
              <div style={{ fontSize: '12px', color: COLORS.danger, marginBottom: '12px' }}>
                {modalError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setModal(null)}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500',
                  border: `1px solid ${COLORS.border}`, borderRadius: '6px',
                  backgroundColor: 'transparent', color: COLORS.textSecondary, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitModal}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  backgroundColor: modal.mode === 'suspend' ? COLORS.danger : COLORS.success,
                  color: 'white',
                  opacity: modalLoading ? 0.6 : 1,
                }}
              >
                {modalLoading ? '…' : modal.mode === 'suspend' ? 'Suspend' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
          {loading ? '…' : `${filtered.length} employer${filtered.length !== 1 ? 's' : ''}`}
        </div>
        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            backgroundColor: COLORS.sidebar,
            border: `1px solid ${COLORS.border}`,
            color: COLORS.textPrimary,
            padding: '8px 14px',
            borderRadius: '6px',
            fontSize: '13px',
            outline: 'none',
            width: '260px',
          }}
        />
      </div>

      <div style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: COL, padding: '0 20px', borderBottom: `1px solid ${COLORS.border}` }}>
          {['Name', 'Email', 'Status', 'Access Level', 'Actions'].map(h => (
            <div key={h} style={headerCell}>{h}</div>
          ))}
        </div>

        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: COL, padding: '14px 20px', borderBottom: `1px solid ${COLORS.border}30` }}>
              {Array.from({ length: 5 }).map((__, j) => <div key={j}><Skeleton /></div>)}
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
            No employers found.
          </div>
        ) : (
          filtered.map((e, i) => (
            <div key={e.id} onClick={() => navigate(`/employers/${e.id}`)} style={{
              display: 'grid',
              gridTemplateColumns: COL,
              padding: '0 20px',
              borderBottom: i < filtered.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
              alignItems: 'center',
              cursor: 'pointer',
            }}>
              <div style={cell}>
                <div style={{ fontWeight: '500' }}>{e.displayName || '(No name)'}</div>
              </div>
              <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '12px' }}>{e.email || '—'}</div>
              <div style={cell}><StatusBadge status={e.accountStatus} /></div>
              <div style={cell}><AccessBadge level={e.accessLevel} /></div>
              <div style={cell} onClick={ev => ev.stopPropagation()}>
                {e.accountStatus === 'suspended' ? (
                  <button
                    onClick={() => openModal(e.id, e.displayName || e.email, 'reactivate')}
                    style={{ background: 'none', border: 'none', color: COLORS.success, fontSize: '12px', cursor: 'pointer', padding: 0, fontWeight: '500' }}
                  >
                    Reactivate
                  </button>
                ) : e.accountStatus === 'active' ? (
                  <button
                    onClick={() => openModal(e.id, e.displayName || e.email, 'suspend')}
                    style={{ background: 'none', border: 'none', color: COLORS.danger, fontSize: '12px', cursor: 'pointer', padding: 0, fontWeight: '500' }}
                  >
                    Suspend
                  </button>
                ) : (
                  <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>—</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
